import {getStaticToolName, isStaticToolUIPart} from 'ai';
import type {CopilotUIMessage} from '@mixpanel/ai-copilot/agent/types';

/**
 * Canonical entity-ref pattern: `toolCallId:toolType:[[1-based-index]]`
 * Used by both the markdown remark plugin (text-content citations) and the
 * ask-user-question option lookup. Single source of truth.
 *
 * Two forms exported:
 * - ENTITY_REF_REGEX_GLOBAL: `g` flag, used by remark `findAndReplace` to walk a string.
 * - ENTITY_REF_REGEX: no flags, used by `.exec()` for single-value lookup.
 *
 * Unanchored: tolerates leading/trailing text the LLM sometimes adds (e.g.
 * "🖥️ OS: call_…:[[1]]" or "call_…:[[1]] = Mobile Safari").
 */
export const ENTITY_REF_REGEX_GLOBAL = /([\w-]+):(\w+):\[\[(\d+)\]\]/g;
export const ENTITY_REF_REGEX = /([\w-]+):(\w+):\[\[(\d+)\]\]/;

type NamedEntity = {name?: string};

function isNamedEntity(value: unknown): value is NamedEntity {
  return typeof value === `object` && value !== null;
}

export type EntityRefMatch = {entity: NamedEntity; toolType: string};

/**
 * Walk `messages` for a tool result with the given `toolCallId` and `toolType`,
 * then return `output.results[entityIndex]` if shape matches. Returns null if
 * any part of the lookup fails.
 *
 * @param entityIndex zero-based.
 */
export function getEntityByRef(
  toolCallId: string,
  toolType: string,
  entityIndex: number,
  messages: CopilotUIMessage[],
): NamedEntity | null {
  for (const msg of messages) {
    for (const part of msg.parts) {
      if (!isStaticToolUIPart(part) || part.state !== `output-available` || getStaticToolName(part) !== toolType) {
        continue;
      }
      if (part.toolCallId !== toolCallId) {
        continue;
      }
      const output = part.output;
      const results = (output as {results?: unknown} | undefined)?.results;
      if (!Array.isArray(results) || entityIndex < 0 || entityIndex >= results.length) {
        continue;
      }
      const entity = results[entityIndex];
      if (isNamedEntity(entity)) {
        return entity;
      }
    }
  }
  return null;
}

/**
 * Match the first entity ref in `option` and resolve it via `getEntityByRef`.
 * Returns null if no ref matches OR the resolved entity is missing.
 */
export function findEntityForRef(option: string, messages: CopilotUIMessage[]): EntityRefMatch | null {
  const match = ENTITY_REF_REGEX.exec(option);
  if (!match) {
    return null;
  }
  const [, toolCallId, toolType, indexStr] = match;
  const entity = getEntityByRef(toolCallId, toolType, parseInt(indexStr, 10) - 1, messages);
  return entity ? {entity, toolType} : null;
}

/** Strip surrounding text and return only the canonical entity-ref substring. */
export function extractEntityRef(option: string): string | null {
  const match = ENTITY_REF_REGEX.exec(option);
  return match?.[0] ?? null;
}

/** Resolve to the entity's `name` field, falling back to the original option string. */
export function resolveEntityName(option: string, messages: CopilotUIMessage[]): string {
  const resolved = findEntityForRef(option, messages);
  if (!resolved) {
    return option;
  }
  return resolved.entity.name || option;
}
